import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { collectionGroup, query, where, onSnapshot } from 'firebase/firestore';
import { db } from '../../firebase';

const TOPICS = [
  { key: 'communication', label: 'Communication' },
  { key: 'hotel', label: 'Hotel' },
  { key: 'food', label: 'Food' },
  { key: 'activities', label: 'Activities' },
  { key: 'sessions', label: 'Training sessions' },
  { key: 'fixtures', label: 'Fixtures' },
  { key: 'transport', label: 'Transport' },
  { key: 'tourLeader', label: 'Tour leader' },
  { key: 'overall', label: 'Overall' },
];

function average(nums) {
  if (!nums.length) return null;
  return nums.reduce((a, b) => a + b, 0) / nums.length;
}

function formatDate(str) {
  if (!str) return '—';
  const [y, m, d] = str.split('-');
  return `${d}/${m}/${y}`;
}

export default function FeedbackOverview() {
  const [responses, setResponses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [leaderFilter, setLeaderFilter] = useState('');

  useEffect(() => {
    const q = query(collectionGroup(db, 'survey'), where('status', '==', 'completed'));
    const unsub = onSnapshot(q, (snap) => {
      const rows = snap.docs
        .map((d) => ({ id: d.id, requestId: d.ref.parent.parent.id, ...d.data() }))
        .sort((a, b) => (b.respondedAt?.seconds ?? 0) - (a.respondedAt?.seconds ?? 0));
      setResponses(rows);
      setLoading(false);
    });
    return unsub;
  }, []);

  const tourLeaders = useMemo(() => {
    const names = new Set(responses.map((r) => r.tourLeaderName).filter(Boolean));
    return Array.from(names).sort();
  }, [responses]);

  const filtered = leaderFilter ? responses.filter((r) => r.tourLeaderName === leaderFilter) : responses;

  const averages = useMemo(() => {
    const result = {};
    TOPICS.forEach(({ key }) => {
      result[key] = average(filtered.map((r) => r.scores?.[key]).filter((v) => v !== undefined));
    });
    return result;
  }, [filtered]);

  if (loading) {
    return <p className="text-[13px] text-[#9CA3AF]">Loading…</p>;
  }

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <h2 className="text-[18px] font-bold text-[#111827]">Feedback Overview</h2>
        <select
          value={leaderFilter}
          onChange={(e) => setLeaderFilter(e.target.value)}
          className="border border-[#E5E7EB] rounded-[8px] px-3 py-1.5 text-[13px] focus:outline-none focus:ring-2 focus:ring-pb"
        >
          <option value="">All tour leaders</option>
          {tourLeaders.map((name) => (
            <option key={name} value={name}>{name}</option>
          ))}
        </select>
      </div>

      {filtered.length === 0 ? (
        <p className="text-[13px] text-[#9CA3AF]">No survey responses yet.</p>
      ) : (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-3">
            {TOPICS.map(({ key, label }) => (
              <div key={key} className="bg-white rounded-[12px] border border-[#E5E7EB] p-4 text-center">
                <p className="text-[10.5px] font-semibold uppercase tracking-[0.5px] text-[#9CA3AF] mb-1">{label}</p>
                <p className="text-[22px] font-bold text-[#111827]">
                  {averages[key] !== null ? averages[key].toFixed(1) : '—'}
                </p>
              </div>
            ))}
          </div>

          <div className="bg-white rounded-[14px] border border-[#E5E7EB] overflow-hidden overflow-x-auto">
            <table className="w-full text-[13px]">
              <thead>
                <tr className="bg-surface text-[11px] uppercase tracking-[0.5px] text-[#9CA3AF]">
                  <th className="text-left px-4 py-3">Tour</th>
                  <th className="text-left px-4 py-3">Dates</th>
                  <th className="text-left px-4 py-3">Tour leader</th>
                  {TOPICS.map(({ key, label }) => (
                    <th key={key} className="text-center px-3 py-3">{label}</th>
                  ))}
                  <th className="text-left px-4 py-3">Responded</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#F1F3F6]">
                {filtered.map((r) => (
                  <tr key={r.requestId} className="hover:bg-surface transition">
                    <td className="px-4 py-3">
                      <Link to={`/staff/requests/${r.requestId}`} className="font-medium text-pb hover:text-navy">
                        {r.tourName || r.requestId}
                      </Link>
                    </td>
                    <td className="px-4 py-3 text-[#6B7280] whitespace-nowrap">{formatDate(r.departureDate)} – {formatDate(r.returnDate)}</td>
                    <td className="px-4 py-3 text-[#6B7280]">{r.tourLeaderName || '—'}</td>
                    {TOPICS.map(({ key }) => (
                      <td key={key} className="px-3 py-3 text-center font-semibold text-[#111827]">
                        {r.scores?.[key] ?? '—'}
                      </td>
                    ))}
                    <td className="px-4 py-3 text-[#9CA3AF] whitespace-nowrap">
                      {r.respondedAt?.seconds
                        ? new Date(r.respondedAt.seconds * 1000).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
                        : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
